import { Match, Show, Switch } from "solid-js";
import { DenominationsGridV2Provider } from "./context/denominations_grid_v2_context";
import { GcAmountHeader } from "./denominations_grid_v2_fragments/gc_amount_header";
import { AmountSelector } from "./denominations_grid_v2_fragments/amount_selector";
import { AmountSelectorLonger } from "./denominations_grid_v2_fragments/amount_selector_longer";
import { DenominationsSelector } from "./denominations_grid_v2_fragments/denomination_selector";
import { GcAmountFooter } from "./denominations_grid_v2_fragments/gc_amount_footer";
import useDenominationsGridV2, {
  DenominationPropsV2,
} from "~/hooks/denominations_grid_v2/useDenominationsGrid";

/**
 * DenominationsGridV2 - New version following Telescope Design System
 *
 * This component uses the same props and hook as the original DenominationsGrid
 * but features a new UI with:
 * - Card-based layout with brand-colored header
 * - Telescope design system tokens
 * - Integrated amount display in header
 * - Cleaner, more modern styling
 */
export function DenominationsGridV2(props: DenominationPropsV2) {
  // const gridState = useDenominationsGridDeprecated(props);
  const gridState = useDenominationsGridV2(props);
  const {
    isVariantBrand,
    isCustomDenominationAllowed,
    isFixedDenominationBrand,
    maxVouchersPerOrder,
  } = gridState;

  // Determine if we should show header with free text (editable amount)
  const isFreeTextHeader = () => {
    // Free text when custom denomination is allowed and denominations list is empty
    return isCustomDenominationAllowed;
  };

  // When min == max on a fixed-denomination brand, the amount is fully
  // determined by defaults — the +/- rows offer no real choice, so hide them.
  const isSingleFixedAmount =
    isFixedDenominationBrand &&
    props.brand.amountConditions.minAmount ===
      props.brand.amountConditions.maxAmount;

  // Extract brand color from brand metadata or use default
  const getBrandColor = () => {
    // Try to get brand color from brand metadata
    // Fallback to a default dark color if not available
    return props.brand.voucherProductMetadata.cardBackgroundColor;
    // return metadata.brandColor || "#1f3647";
  };

  return (
    <DenominationsGridV2Provider value={gridState}>
      {/* Outer container with padding */}
      <div class="w-full rounded-2xl bg-background-mapping-reverse p-1">
        {/* Inner card */}
        <div class="flex w-full flex-col gap-4 rounded-xl border border-stroke-1 bg-background-normal-primary p-3">
          {/* Header with brand logo and amount */}
          <GcAmountHeader
            brandTitle={props.brand.voucherProductMetadata.title}
            brandLogoUrl={props.brand.voucherProductMetadata.sqLogoUrl}
            brandColor={getBrandColor()}
            isFreeText={isFreeTextHeader()}
          />

          <Show when={!isSingleFixedAmount}>
            <div class="flex w-full flex-col gap-3">
              <div class="text-center text-para-3-regular tracking-[-0.14px] text-text-normal-secondary">
                Gift card value
              </div>

              <Switch>
                <Match when={isVariantBrand}>
                  <AmountSelectorLonger />
                </Match>
                <Match
                  when={
                    isFixedDenominationBrand &&
                    // we show AmountSelector when maxVouchersPerOrder is 1
                    maxVouchersPerOrder > 1
                  }
                >
                  <DenominationsSelector />
                </Match>
                <Match when={true}>
                  <AmountSelector />
                </Match>
              </Switch>
            </div>
          </Show>

          {/* Footer with coins/payment info and CTA */}
          <GcAmountFooter />
        </div>
      </div>
    </DenominationsGridV2Provider>
  );
}
